if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.handleApplyClick = this.applyPriceRange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleBlur = this.handleBlur.bind(this);
      this.handleActiveFilterClick = this.handleActiveFilterClick.bind(this);
      
      this.priceState = {
        min: '',
        max: ''
      };

      this.isInitialized = false;
      this.isUpdating = false;

      this.boundLoadAllFirstVariantPrices = this.loadAllFirstVariantPrices.bind(this);

      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.minNumberInput = this.querySelector('#price-range-number-min');
      this.maxNumberInput = this.querySelector('#price-range-number-max');
      this.applyButton = this.querySelector('#price-range-apply-button');
      
      this.initializeFromURL();
      this.syncPriceState();
      
      this.setupPriceObserver();

      this.addListeners();
      this.restoreMaxPricePlaceholder();

      // Check both window and component storage
      if (window.firstVariantPrices && Array.isArray(window.firstVariantPrices)) {
        this.loadAllFirstVariantPrices({ detail: { prices: window.firstVariantPrices } });
      }
      
      // Listen for both window and component events
      window.addEventListener('firstVariantPricesLoaded', this.loadAllFirstVariantPrices.bind(this));
      this.addEventListener('firstVariantPricesLoaded', this.loadAllFirstVariantPrices.bind(this));
    }

    addListeners() {
      this.minNumberInput.addEventListener('input', this.handleInput);
      this.maxNumberInput.addEventListener('input', this.handleInput);
      this.minNumberInput.addEventListener('blur', this.handleBlur);
      this.maxNumberInput.addEventListener('blur', this.handleBlur);

      const activeFilters = this.closest('.facets')?.querySelector('.facets__active-filters');
      if (activeFilters) {
        activeFilters.addEventListener('click', this.handleActiveFilterClick);
      }
    }

    setupPriceObserver() {
      const config = { 
        childList: true, 
        subtree: true,
        characterData: true 
      };
      
      this.observer = new MutationObserver((mutations) => {
        this.updatePriceDisplay();
      });

      const facets = this.closest('.facets');
      const activeFilters = facets?.querySelector('.facets__active-filters');
      
    }

    initializeFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const minPrice = urlParams.get('filter.p.m.custom.price_min');
      const maxPrice = urlParams.get('filter.p.m.custom.price_max');

      if (minPrice) {
        this.priceState.min = Number(minPrice).toFixed(2);
        this.minNumberInput.value = this.priceState.min;
      }

      if (maxPrice) {
        this.priceState.max = Number(maxPrice).toFixed(2);
        this.maxNumberInput.value = this.priceState.max;
      }

      this.updatePriceDisplay();
    }

    updatePriceDisplay() {
      const facets = document.querySelector('.facets');
      const activeFilters = facets?.querySelector('.facets__active-filters');

      if (!activeFilters) return;

      let priceFilter = activeFilters.querySelector('.js-price-filter');
      if (!priceFilter) {
        activeFilters.insertAdjacentHTML('beforeend', `
          <a href="#" class="active-filter flex items-center text-current js-price-filter hidden" id="price-range" data-key="filter.p.m.custom.first_variant_price" data-filter-type="price" data-params="">
            <span>
              <span class="price-range-min">${this.priceState.min || '0.00'}</span> - 
              <span class="price-range-max">${this.priceState.max || (this.allFirstVariantPrices ? Math.max(...this.allFirstVariantPrices).toFixed(2) : '')}</span>
            </span>
            ${this.getCloseIconHTML()}
          </a>
        `);
        priceFilter = activeFilters.querySelector('.js-price-filter');
      }

      const minDisplay = priceFilter.querySelector('.price-range-min');
      const maxDisplay = priceFilter.querySelector('.price-range-max');


      if (this.priceState.min || this.priceState.max) {
        // Show filter if there are active values
        priceFilter.classList.remove('hidden');
        activeFilters.hidden = false;

        if (minDisplay) {
          minDisplay.textContent = this.priceState.min || '0.00';
        }
        if (maxDisplay) {
          maxDisplay.textContent = this.priceState.max || 
            (this.allFirstVariantPrices ? Math.max(...this.allFirstVariantPrices).toFixed(2) : '');
        }
      }

      // Update filter counter
      const activeFiltersCount = activeFilters.querySelectorAll('.active-filter:not(.hidden)').length;
      const activeFiltersHeader = activeFilters.querySelector('.active-filters-header');
      
      if (activeFiltersHeader) {
        if (activeFiltersCount > 0) {
          activeFiltersHeader.textContent = theme.strings.appliedFilterCount?.replace('[count]', activeFiltersCount) 
            || `${activeFiltersCount} applied filters`;
        } else {
          activeFiltersHeader.textContent = theme.strings.appliedFilters || 'Applied filters';
        }
      }
    }

    checkInputs() {
      const minVal = this.minNumberInput.value.trim();
      const maxVal = this.maxNumberInput.value.trim();
      
      const hasValidInput = (minVal !== '' && !isNaN(parseFloat(minVal))) || 
                           (maxVal !== '' && !isNaN(parseFloat(maxVal)));
      
      
    }

    async applyPriceRange() {
      if (this.isUpdating) {
        console.log('Price range update already in progress, skipping...');
        return;
      }

      this.isUpdating = true;

      if (!this.isInitialized) {
        try {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Timeout waiting for prices'));
            }, 5000);

            const handler = (event) => {
              if (event.detail?.prices) {
                clearTimeout(timeout);
                this.allFirstVariantPrices = event.detail.prices;
                this.isInitialized = true;
                resolve();
              }
            };

            this.addEventListener('firstVariantPricesLoaded', handler, { once: true });
            console.log('Waiting for prices...');
          });
        } catch (error) {
          console.error('Error waiting for prices:', error);
          return;
        }
      }

      let minInput = this.minNumberInput.value.trim();
      let maxInput = this.maxNumberInput.value.trim();

      let minPrice = minInput ? parseFloat(minInput) : 0;
      let maxPrice = maxInput ? parseFloat(maxInput) : Math.max(...this.allFirstVariantPrices);

      const formattedMinPrice = minPrice === 0 ? "0.00" : Number(minPrice).toFixed(2);
      const formattedMaxPrice = Number(maxPrice).toFixed(2);

      this.minNumberInput.value = formattedMinPrice;
      this.maxNumberInput.value = maxInput || '';
      
      this.priceState = {
        min: formattedMinPrice,
        max: formattedMaxPrice
      };

      const pricesInRange = this.allFirstVariantPrices.filter(price => {
        const numericPrice = parseFloat(price);
        return numericPrice >= minPrice && numericPrice <= maxPrice;
      });

      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      params.delete('filter.p.m.custom.first_variant_price');
      params.delete('filter.p.m.custom.price_min');
      params.delete('filter.p.m.custom.price_max');
      
      if (pricesInRange.length > 0) {
        const formattedPrices = pricesInRange.map(price => Number(price).toFixed(2));
        const priceParam = formattedPrices.join(',');
        params.set('filter.p.m.custom.first_variant_price', priceParam);
      }
      
      if (this.priceState.min !== '' || this.priceState.max !== '') {
        params.set('filter.p.m.custom.price_min', formattedMinPrice);
        params.set('filter.p.m.custom.price_max', formattedMaxPrice);
      }

      const results = document.getElementById('filter-results');
      const customPagination = document.querySelector('custom-pagination');
      const facetDrawer = document.querySelector('facet-filters');
      
      if (customPagination) {
        customPagination.dataset.pauseInfiniteScroll = 'true';
      }

      results.classList.add('is-loading');
      if (facetDrawer) facetDrawer.classList.add('is-loading');

      if (facetDrawer && facetDrawer.isUpdating) {
        console.log('Facet filters update in progress, skipping price range update...');
        return;
      }

      try {
        const fetchUrl = new URL(window.location.pathname, window.location.origin);
        const cleanParams = new URLSearchParams(params.toString());
        const fetchUrlString = `${fetchUrl.pathname}?${cleanParams.toString()}`.replace(/%2C/g, ',');

        const response = await fetch(fetchUrlString);
        
        if (!response.ok) {
          throw new Error(response.status);
        }

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const fetchedResults = tmpl.content.getElementById('filter-results');
        if (fetchedResults) {
          results.innerHTML = fetchedResults.innerHTML;
        }

        const facetFilters = document.querySelector('facet-filters');
        if (facetFilters && facetFilters.filteringEnabled) {
          facetFilters.updateFilters(tmpl.content);
        }

        const newUrl = `${window.location.pathname}?${params.toString()}`.replace(/%2C/g, ',');
        window.history.pushState({ searchParams: params.toString() }, '', newUrl);

        if (typeof initLazyImages === 'function') {
          initLazyImages();
        }

        if (customPagination?.reload) {
          customPagination.reload();
        }

        this.syncPriceState();
        this.updatePriceDisplay();
        
        setTimeout(() => {
          this.handleHideMobileDrawer();
        }, 300);

        document.dispatchEvent(
          new CustomEvent('on:facet-filters:updated', { bubbles: true })
        );


        if (results.getBoundingClientRect().top < 0) {
          const headerOffset = this.getHeaderOffset();
          window.scrollTo({
            top: results.offsetTop - headerOffset - 20,
            behavior: 'smooth'
          });
        }

      } catch (error) {
        console.error('Error applying price range filter:', error);
      } finally {
        this.isUpdating = false;
        results.classList.remove('is-loading');
        if (facetDrawer) facetDrawer.classList.remove('is-loading');
        if (customPagination) {
          customPagination.dataset.pauseInfiniteScroll = 'false';
        }
      }
    }

    loadAllFirstVariantPrices(event) {
      if (this.isInitialized) return; // Prevent multiple initializations
      
      if (event?.detail?.prices && Array.isArray(event.detail.prices)) {
        this.allFirstVariantPrices = event.detail.prices;
        this.applyButton.disabled = false;
        this.updateMaxPricePlaceholder();
        this.isInitialized = true;
      } else if (window.firstVariantPrices && Array.isArray(window.firstVariantPrices)) {
        this.allFirstVariantPrices = window.firstVariantPrices;
        this.applyButton.disabled = false;
        this.updateMaxPricePlaceholder();
        this.isInitialized = true;
      }
    }

    disconnectedCallback() {
      // Only remove event listeners if the input elements exist
      if (this.minNumberInput) {
        this.minNumberInput.removeEventListener('input', this.handleInput);
        this.minNumberInput.removeEventListener('blur', this.handleBlur);
      }
      if (this.maxNumberInput) {
        this.maxNumberInput.removeEventListener('input', this.handleInput);
        this.maxNumberInput.removeEventListener('blur', this.handleBlur);
      }

      // Remove firstVariantPricesLoaded listeners using the stored bound function
      if (this.boundLoadAllFirstVariantPrices) {
        window.removeEventListener('firstVariantPricesLoaded', this.boundLoadAllFirstVariantPrices);
        this.removeEventListener('firstVariantPricesLoaded', this.boundLoadAllFirstVariantPrices);
      }

      const activeFilters = this.closest('.facets')?.querySelector('.facets__active-filters');
      if (activeFilters) {
        activeFilters.removeEventListener('click', this.handleActiveFilterClick);
      }

      if (this.observer) {
        this.observer.disconnect();
      }
    }

    updateFilters(html) {
      const fetchedPriceRange = html.querySelector('price-range');
      if (fetchedPriceRange) {
        const fetchedMinInput = fetchedPriceRange.querySelector('#price-range-number-min');
        const fetchedMaxInput = fetchedPriceRange.querySelector('#price-range-number-max');

        if (fetchedMinInput && this.minNumberInput) {
          this.minNumberInput.value = fetchedMinInput.value;
        }
        if (fetchedMaxInput && this.maxNumberInput) {
          this.maxNumberInput.value = fetchedMaxInput.value;
        }
      }

      this.updateActiveFilters(html);

      const footerEl = html.querySelector('.facets__footer');
      if (footerEl && this.footer) {
        this.footer.innerHTML = footerEl.innerHTML;
      }
    }

    static updateFilter(filter, fetchedFilter, updateAll) {
      if (fetchedFilter) {
        filter.hidden = false;
        if (updateAll) {
          const currentMin = filter.querySelector('#price-range-number-min').value;
          const currentMax = filter.querySelector('#price-range-number-max').value;

          filter.innerHTML = fetchedFilter.innerHTML;

          const minInput = filter.querySelector('#price-range-number-min');
          const maxInput = filter.querySelector('#price-range-number-max');
          if (minInput) minInput.value = currentMin;
          if (maxInput) maxInput.value = currentMax;
        }
      } else {
        filter.hidden = true;
      }
    }

    updateActiveFilters(html) {
      const activeFilters = this.closest('.facets')?.querySelector('.facets__active-filters');
      const activeFiltersList = activeFilters?.querySelector('.active-filters');
      const activeFiltersHeader = activeFilters?.querySelector('.active-filters-header');

      const newActiveFiltersList = html.querySelector('.active-filters');
      const newActiveFiltersHeader = html.querySelector('.active-filters-header');

      if (activeFiltersList && newActiveFiltersList) {
        let existingPriceFilter = activeFiltersList.querySelector('#price-range');
        const minPrice = this.priceState.min || this.minNumberInput.value;
        const maxPrice = this.priceState.max || this.maxNumberInput.value;

        if (minPrice || maxPrice) {
          if (!existingPriceFilter) {
            existingPriceFilter = document.createElement('a');
            existingPriceFilter.id = 'price-range';
            existingPriceFilter.className = 'active-filter flex items-center text-current';
            existingPriceFilter.href = '#';
            existingPriceFilter.dataset.key = 'filter.p.m.custom.first_variant_price';
            activeFiltersList.appendChild(existingPriceFilter);
          }

          const formattedMinPrice = minPrice ? parseFloat(minPrice).toFixed(2) : '0.00';
          const formattedMaxPrice = maxPrice ? parseFloat(maxPrice).toFixed(2) : 
                                   (this.allFirstVariantPrices ? Math.max(...this.allFirstVariantPrices).toFixed(2) : '');

          existingPriceFilter.innerHTML = `
            <span>
              <span class="price-range-min">${formattedMinPrice}</span> - 
              <span class="price-range-max">${formattedMaxPrice}</span>
            </span>
            ${this.getCloseIconHTML()}
          `;
        } else if (existingPriceFilter) {
          existingPriceFilter.remove();
        }

        const otherFilters = Array.from(newActiveFiltersList.children).filter(
          child => child.id !== 'price-range'
        );

        Array.from(activeFiltersList.children).forEach(child => {
          if (child.id !== 'price-range') {
            child.remove();
          }
        });

        otherFilters.forEach(filter => {
          activeFiltersList.appendChild(filter.cloneNode(true));
        });

        const filterCount = activeFiltersList.querySelectorAll('.active-filter').length;
        if (filterCount > 0) {
          activeFiltersHeader.textContent = theme.strings.appliedFilterCount?.replace('[count]', filterCount) 
            || `${filterCount} applied filters`;
          activeFilters.hidden = false;
        } else {
          activeFiltersHeader.textContent = theme.strings.appliedFilters || 'Applied filters';
          activeFilters.hidden = true;
        }
      }
    }

    getCloseIconHTML() {
      return `<svg class="icon icon-close" aria-hidden="true" focusable="false">
        <use href="#icon-close"/>
      </svg>`;
    }

    handleActiveFilterClick(event) {
      const filterLink = event.target.closest('.js-price-filter');
      if (!filterLink) return;
      
      event.preventDefault();
      
      this.priceState = { min: '', max: '' };
      this.minNumberInput.value = '';
      this.maxNumberInput.value = '';
      
      filterLink.classList.add('hidden');
      
      const activeFilters = this.closest('.facets')?.querySelector('.facets__active-filters');
      if (activeFilters) {
        const hasOtherActiveFilters = activeFilters.querySelectorAll('.active-filter:not(.hidden)').length > 0;
        activeFilters.hidden = !hasOtherActiveFilters;
        
        // Update URL and apply filters
        const facetFilters = this.closest('facet-filters');
        if (facetFilters) {
          const searchParams = new URLSearchParams(window.location.search);
          ['filter.p.m.custom.first_variant_price', 'filter.p.m.custom.price_min', 'filter.p.m.custom.price_max'].forEach(param => {
            searchParams.delete(param);
          });
          
          facetFilters.applyFilters(searchParams.toString());
        }
      }
    }

    handleInput(event) {
      event.stopPropagation();
      
      const input = event.target;
      let value = input.value.trim();
      
      // Remove all characters except digits and dot
      value = value.replace(/[^\d.]/g, '');
      
      // Keep only first dot
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Allow any valid number input
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        input.value = value;
      } else {
        input.value = '';
      }
      
      this.checkInputs();
    }

    preventSubmit(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    }

    getHeaderOffset() {
      const stickyHeader = document.querySelector('store-header[data-is-sticky="true"]');
      if (!stickyHeader) return 0;

      return parseInt(
        getComputedStyle(stickyHeader.parentElement)
          .getPropertyValue('--header-height')
          .replace('px', ''),
        10
      ) || 0;
    }

    restoreMaxPricePlaceholder() {
      if (this.allFirstVariantPrices?.length > 0) {
        this.updateMaxPricePlaceholder();
      } else {
        window.addEventListener('firstVariantPricesLoaded', (event) => {
          if (event.detail?.prices?.length > 0) {
            this.updateMaxPricePlaceholder();
          }
        }, { once: true });
      }
    }

    updateMaxPricePlaceholder() {
      if (this.maxNumberInput && this.allFirstVariantPrices?.length > 0) {
        const maxPrice = Math.max(...this.allFirstVariantPrices);
        this.maxNumberInput.placeholder = maxPrice.toFixed(2);
      }
    }

    handleBlur(event) {
      const input = event.target;
      let value = input.value.trim();
      
      if (value) {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
          // Keep original value without forcing 2 decimal places
          input.value = value;
        } else {
          input.value = '';
        }
      }
      
      this.checkInputs();
    }

    syncPriceState() {
      const urlParams = new URLSearchParams(window.location.search);
      const minPrice = urlParams.get('filter.p.m.custom.price_min');
      const maxPrice = urlParams.get('filter.p.m.custom.price_max');

      this.priceState = {
        min: minPrice || '',
        max: maxPrice || ''
      };

      // Update input values
      if (this.minNumberInput) {
        this.minNumberInput.value = this.priceState.min;
      }
      if (this.maxNumberInput) {
        this.maxNumberInput.value = this.priceState.max;
      }

      this.updatePriceDisplay();
    }


    handleHideMobileDrawer() {
      if(window.innerWidth < 768) {
          const facetFilters = document.querySelector('facet-filters');
          if (facetFilters) {
            facetFilters.removeAttribute('open');
          }

          const overlay = document.querySelectorAll('.overlay');
          if (overlay) {
            overlay.forEach(item => {
              item.classList.remove('is-visible');
            });
          }

          const body = document.body;
          body.style.position = '';
          body.style.overflow = '';
          body.classList.remove('fixed');
      }
    }
  }

  customElements.define('price-range', PriceRange);
}